import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ar } from './ar';
import { en } from './en';

/**
 * Arabic-first localisation.
 *
 * Deliberately small: no runtime library, no ICU compiler. The catalogues are
 * plain objects keyed by `namespace.key`, so a feature branch adds
 * `src/i18n/<epic>.ar.ts` and merges it in `ar.ts` - one line of a shared file
 * per epic instead of a shared mega-catalogue that eleven branches all rewrite.
 *
 * `ar` is the default and the fallback. A missing English string renders the
 * Arabic one rather than the raw key, because a clinic switching to English
 * mid-shift should see a usable screen, not `nav.dashReception`.
 */

export type Locale = 'ar' | 'en';
export type Direction = 'rtl' | 'ltr';

export const DIRECTION: Record<Locale, Direction> = { ar: 'rtl', en: 'ltr' };

type Catalogue = Record<string, string>;

const CATALOGUES: Record<Locale, Catalogue> = { ar, en };

const LOCALE_KEY = 'medos.locale';

interface I18nValue {
  locale: Locale;
  dir: Direction;
  setLocale: (locale: Locale) => void;
  /** Translate. `vars` interpolates `{name}` placeholders. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Locale-aware number formatting. Arabic uses Eastern Arabic numerals. */
  n: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Money, always with the tenant's currency code. */
  money: (value: number, currency: string) => string;
  /** Date/time in the given IANA zone - never the browser's. */
  date: (value: Date | string, options?: Intl.DateTimeFormatOptions, timeZone?: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function I18nProvider({
  children,
  /**
   * The tenant's time zone. Scheduling and clinical timestamps resolve against
   * the branch, never the browser: a receptionist travelling must not see
   * appointments shift (PRD 18.1).
   */
  timeZone = 'Asia/Riyadh',
}: {
  children: ReactNode;
  timeZone?: string;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_KEY) : null;
    return stored === 'en' || stored === 'ar' ? stored : 'ar';
  });

  const dir = DIRECTION[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(LOCALE_KEY, next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const intlLocale = locale === 'ar' ? 'ar-SA' : 'en-GB';

    return {
      locale,
      dir,
      setLocale,
      t: (key, vars) => {
        const catalogue = CATALOGUES[locale];
        const fallback = CATALOGUES.ar;
        return interpolate(catalogue[key] ?? fallback[key] ?? key, vars);
      },
      n: (val, options) => new Intl.NumberFormat(intlLocale, options).format(val),
      money: (val, currency) =>
        new Intl.NumberFormat(intlLocale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        }).format(val),
      date: (val, options, zone) =>
        new Intl.DateTimeFormat(intlLocale, {
          timeZone: zone ?? timeZone,
          ...options,
        }).format(typeof val === 'string' ? new Date(val) : val),
    };
  }, [locale, dir, setLocale, timeZone]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for the overwhelmingly common case. */
export function useT(): I18nValue['t'] {
  return useI18n().t;
}
